import { useCallback, useEffect, useMemo, useState } from 'react';
import { Eye, MessagesSquare } from 'lucide-react';
import { canSpeak, conversationsApi, isOversight, unitTransport } from '../api/conversations';
import type { ConversationDetail } from '../api/conversations';
import { ApiError } from '../api/client';
import type { Message } from '../api/client';
import { unitModeration } from '../api/moderation';
import { useConversation } from '../hooks/useConversation';
import { useUnreadAnchor } from '../hooks/useUnreadAnchor';
import { AppBar, Footer, Gutter, Page } from '../ui/Chrome';
import { RefusalCard } from '../ui/ComplianceNotice';
import { EmptyState } from '../ui/EmptyState';
import { MessageBubble } from '../ui/MessageBubble';
import { AnchorLink } from '../ui/AnchorLink';
import { ParticipantStrip } from '../ui/ParticipantStrip';
import { Composer } from './Composer';
import { MessageActions } from './MessageActions';
import { messageRuns } from '../lib/messageRuns';
import type { Me } from '../api/auth';

/**
 * The thread, on live data.
 *
 * Participants come from the API's derived view rather than a stored list, so a
 * leader who joins the unit tomorrow appears here without anybody editing this
 * thread — and the monitor roster is rendered rather than hidden, per §7.
 */
export function LiveThreadScreen({
  unitId,
  conversationId,
  me,
  meId,
  onBack,
  onNavigate,
  onOpenAnchor,
}: {
  unitId: string;
  conversationId: string;
  /** The signed-in person, for the account control the bar now carries (TROOP-122). */
  me: Me;
  meId: string;
  onBack: () => void;
  /** For the account control. Separate from `onOpenAnchor`, which means one specific link. */
  onNavigate: (path: string) => void;
  /**
   * Where to go when the reader follows the link to the event or announcement this thread belongs
   * to. The route is the caller's because a member's copy lives under `/units/` and a parent's
   * under `/family/` -- and because whether they may open it is the event route's answer, not this
   * screen's.
   */
  onOpenAnchor?: (path: string) => void;
}) {
  const [detail, setDetail] = useState<ConversationDetail | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  // A 404 held apart from every other failure, because only this one has an answer to offer. The
  // catch used to swallow the error object entirely, so a missing thread, a refused one and a flat
  // network read all produced the same sentence -- which is fine until one of them is actionable.
  const [notFound, setNotFound] = useState(false);

  // Whether this reader has asked to open a thread they are not in, under I3 (TROOP-14). It starts
  // false and is only ever set by the button below, which says in as many words what it does. The
  // server is the judge -- a member setting it by hand gets the same 404 -- so this decides what is
  // *offered*, never what is permitted.
  const [oversight, setOversight] = useState(false);

  // Admin of this unit, from the session. Nothing turns on it but whether the offer is drawn:
  // ConversationAccessPolicy resolves admin standing itself, against the conversation's own unit,
  // which is not necessarily this one.
  const isUnitAdmin =
    me.memberships.find((m) => m.unitId === unitId)?.permissionLevel === 'Admin';

  // The message whose action sheet is open, or null. Held here rather than per bubble so only one
  // sheet can ever exist -- `Overlay` traps focus, and two of them would fight over it.
  const [actingOn, setActingOn] = useState<Message | null>(null);

  // Rebuilt when oversight turns on, which is what makes the message pages carry the flag too --
  // a strip of names without the messages is not what I3 grants.
  const transport = useMemo(() => unitTransport(unitId, oversight), [unitId, oversight]);
  const moderation = useMemo(() => unitModeration(unitId), [unitId]);
  const { messages, status, error, send, reconcile, markRead } = useConversation(
    transport,
    conversationId,
  );

  // Where this thread opens. Captured from the detail before `markRead` moves the marker,
  // which is the whole ordering this depends on (TROOP-99).
  const { firstUnreadId, firstUnread, bottom } = useUnreadAnchor(
    conversationId,
    messages,
    detail?.yourLastReadSeq,
    meId,
  );

  // Who gets a name and who gets a timestamp. Depends on the unread divider as well as on the
  // messages, because a separator ends both runs.
  const runs = useMemo(() => messageRuns(messages, firstUnreadId), [messages, firstUnreadId]);

  // Named, so an edit to who is in the thread can re-read it. Deriving the strip from the rows the
  // panel already holds would not work here: adding somebody changes the derived sets too -- their
  // guardians become readers, and the compliance line moves -- and only the server knows that.
  const reloadDetail = useCallback(() => {
    conversationsApi
      .detail(unitId, conversationId, oversight)
      .then((d) => {
        setDetail(d);
        setNotFound(false);
        setLoadError(null);
      })
      .catch((e: unknown) => {
        // 404 covers "no such thread" and "not yours" alike, deliberately -- a distinguishable
        // refusal would confirm the thread exists, which is most of what a guesser wants. So this
        // cannot say which it is, and the copy below does not pretend to.
        if (e instanceof ApiError && e.status === 404) {
          setNotFound(true);
          return;
        }

        setLoadError('Could not load this conversation.');
      });
  }, [unitId, conversationId, oversight]);

  useEffect(reloadDetail, [reloadDetail]);


  // Marks read only while the thread is genuinely in front of somebody. The visibility guard is
  // the point: without it a background reconcile would clear the badge with the phone in a pocket.
  //
  // And never under oversight (TROOP-14). A read marker is what puts a thread on somebody's unread
  // badge, and an admin's badge counting other people's children's threads is the outcome the whole
  // feature is arranged to avoid -- so the route refuses it server-side and this does not ask.
  const marksRead = !isOversight(detail?.yourBasis);

  useEffect(() => {
    if (messages.length === 0 || !marksRead) {
      return;
    }

    const mark = () => {
      if (document.visibilityState === 'visible') {
        void markRead();
      }
    };

    mark();
    document.addEventListener('visibilitychange', mark);
    return () => document.removeEventListener('visibilitychange', mark);
  }, [messages, markRead, marksRead]);

  if (notFound) {
    return (
      <Page>
        <AppBar
          parentLabel="Conversations"
          title="Conversation"
          onBack={onBack}
          me={me}
          currentUnitId={unitId}
          onNavigate={onNavigate}
        />

        <Gutter>
          <div style={{ padding: 'var(--space-6) 0' }}>
            {/* The only place in the product that offers I3, and it is offered rather than taken.
                A member sees the plain refusal; an admin sees what they can do about it, spelled
                out, because a control that records something must say so before it is pressed --
                and this one puts their name in front of everybody in the thread, youth included.

                The offer is drawn from the session's permission level, so it is drawn for an admin
                on a thread that does not exist too. That is the correct answer: the alternative is
                a control whose presence confirms a thread is there, which is the existence oracle
                the 404 exists to prevent. Pressing it then simply 404s again. */}
            <EmptyState
              icon={Eye}
              headline="This conversation is not yours to read"
              explanation={
                isUnitAdmin
                  ? 'It may not exist, or you may not be in it. As a unit admin you can open it ' +
                    'anyway — but this is recorded, and everybody in the thread is shown that you ' +
                    'read it and when, young people included.'
                  : 'It may not exist, or you may not be in it.'
              }
              action={
                isUnitAdmin
                  ? { label: 'Open it on the record', onClick: () => setOversight(true) }
                  : undefined
              }
            />
          </div>
        </Gutter>

        <Footer />
      </Page>
    );
  }

  if (loadError) {
    return (
      <Page>
        <AppBar
          parentLabel="Conversations"
          title="Conversation"
          onBack={onBack}
          me={me}
          currentUnitId={unitId}
          onNavigate={onNavigate}
        />

        <Gutter>
          <p className="t-body" style={{ color: 'var(--color-text-secondary)', padding: 'var(--space-6) 0' }}>
            {loadError}
          </p>
        </Gutter>

        <Footer />
      </Page>
    );
  }

  return (
    <Page>
      <AppBar
        parentLabel="Conversations"
        title={detail?.title ?? 'Conversation'}
        subtitle={detail ? `${detail.participants.length} people` : undefined}
        onBack={onBack}
        me={me}
        currentUnitId={unitId}
        onNavigate={onNavigate}
      />

      {/* Participant strip, ending in the same 3px rule as the event facts: who is
          present is a fact about the thread, not part of the conversation. */}
      <section className="rule-canonical" style={{ background: 'var(--color-surface-page)' }}>
        <Gutter>
          <ParticipantStrip detail={detail} unitId={unitId} onChanged={reloadDetail} />
          {/* What this thread is about, above the messages and below who is in it. Both are facts
              about the thread rather than part of the conversation. */}
          <AnchorLink
            detail={detail}
            onOpen={(kind, id) =>
              onOpenAnchor?.(kind === 'Event' ? `/units/${unitId}/events/${id}` : `/units/${unitId}`)
            }
          />
        </Gutter>
      </section>

      <section
        className="flex-1"
        style={{ background: 'var(--color-surface-sunken)', paddingBottom: 'var(--space-6)' }}
      >
        <Gutter>
          <div
            className="flex items-center justify-between gap-4"
            style={{ paddingBlock: 'var(--space-4)' }}
          >
            <h2 className="t-label" style={{ color: 'var(--color-text-muted)', margin: 0 }}>
              Discussion &middot; <span className="tabular">{messages.length}</span>
            </h2>
            <span className="t-caption" style={{ color: 'var(--color-text-secondary)' }}>
              {status === 'connected' ? 'Live' : status === 'reconnecting' ? 'Reconnecting' : 'Offline'}
            </span>
          </div>

          {detail?.complianceState === 'Blocked' && (
            <div style={{ marginBottom: 'var(--space-4)' }}>
              <RefusalCard
                headline="This conversation needs a second adult"
                explanation="Threads that include youth need at least two adults present at all times. Add another adult, or close the thread."
                fixLabel="Add an adult"
              />
            </div>
          )}

          {messages.length === 0 ? (
            <EmptyState
              icon={MessagesSquare}
              headline="No messages yet"
              explanation={
                detail?.complianceState === 'Satisfied'
                  ? 'Two adults are already here, so you can start whenever.'
                  : 'Start whenever you’re ready.'
              }
            />
          ) : (
            <div>
              {messages.map((message, i) => (
                <div
                  key={message.id}
                  ref={message.id === firstUnreadId ? firstUnread : undefined}
                >
                  {/* A line rather than a jump. The reader has to be able to see where they left
                      off, or landing in the middle of a thread reads as the page being lost. */}
                  {message.id === firstUnreadId && messages[0]?.id !== message.id && (
                    <div
                      className="flex items-center gap-2"
                      style={{ paddingBlock: 'var(--space-3)' }}
                    >
                      <span
                        aria-hidden="true"
                        style={{ height: 1, flex: 1, background: 'var(--color-accent-announcement)' }}
                      />
                      <span
                        className="t-caption"
                        style={{ color: 'var(--color-accent-announcement)', flexShrink: 0 }}
                      >
                        New
                      </span>
                      <span
                        aria-hidden="true"
                        style={{ height: 1, flex: 1, background: 'var(--color-accent-announcement)' }}
                      />
                    </div>
                  )}

                  <MessageBubble
                    message={{
                      id: message.id,
                      authorName: message.authorDisplayName,
                      authorClassification:
                        detail?.participants.find((p) => p.personId === message.authorPersonId)
                          ?.classification === 'Youth'
                          ? 'youth'
                          : 'adult',
                      // Empty rather than the API's null, and the bubble draws the tombstone from
                      // `removed` instead -- the words are gone, the fact that a message was here
                      // is not (TROOP-12, section 5).
                      body: message.isRemoved
                        ? ''
                        : (message.body ?? ''),
                      time: runs[i].time,
                      own: message.authorPersonId === meId,
                      startsRun: runs[i].startsRun,
                      removed: message.isRemoved,
                      removalBasis: message.removalBasis,
                    }}
                    onOpenActions={() => setActingOn(message)}
                  />
                </div>
              ))}

              {/* Where a caught-up reader lands. An element rather than a scroll height, because
                  the composer sits below this and a height would land above it. */}
              <div ref={bottom} />
            </div>
          )}

          {error && (
            <p
              className="t-caption"
              role="alert"
              style={{ color: 'var(--color-status-blocked)', marginTop: 'var(--space-3)' }}
            >
              {error}{' '}
              <button type="button" className="link-button" onClick={() => void reconcile()}>
                Retry
              </button>
            </p>
          )}

          {canSpeak(detail?.yourBasis) ? (
            <div style={{ marginTop: 'var(--space-5)' }}>
              <Composer onSend={send} />
            </div>
          ) : (
            /* Until TROOP-14 every basis reachable under `/units/` could speak, so this screen had
               no gate at all -- a guardian's I4 read happens on the family door, where the gate
               already exists. AdminOversight is the first one here that reads and must not post,
               and the server refuses it either way; this decides whether the control is offered.

               Not a disabled composer, per the design system: a control that cannot be pressed owes
               an explanation on every render, and naming who does speak here is more use. */
            <p
              className="t-caption"
              style={{
                color: 'var(--color-text-secondary)',
                margin: 'var(--space-5) 0 0',
                maxWidth: '52ch',
              }}
            >
              You are reading this on the record as a unit admin, not as somebody in it. Posting is
              for the people in the thread — everybody here can see that you read it.
            </p>
          )}

          {actingOn && (
            <MessageActions
              message={actingOn}
              transport={moderation}
              onClose={() => setActingOn(null)}
              // A full re-read rather than a local edit: the tombstone, and whether this reader may
              // now put it back, are both the server's answers and neither is derivable here.
              onChanged={() => void reconcile()}
            />
          )}
        </Gutter>
      </section>

      <Footer />
    </Page>
  );
}
